import React, { useRef, useCallback } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Paper,
  Collapse,
  Button,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';

interface InputVariable {
  name: string;
  type?: string;
  description?: string;
}

interface PythonCodeEditorProps {
  /** Current code value */
  value: string;
  /** Callback when code changes */
  onChange: (value: string) => void;
  /** Whether editor is disabled/read-only */
  disabled?: boolean;
  /** Available input variables that will be injected */
  availableInputs?: InputVariable[];
  /** Minimum height of editor */
  minHeight?: number;
  /** Maximum height of editor */
  maxHeight?: number;
  /** Placeholder code to show when empty */
  placeholder?: string;
  /** Callback to test run the code */
  onTestRun?: () => void;
  /** Whether test run is in progress */
  testRunLoading?: boolean;
}

// Python code snippets for quick insertion
const CODE_SNIPPETS = [
  {
    label: 'Result Template',
    code: `# Set the result variable (required)
result = {
    'output': processed_value,
    'status': 'success'
}`,
  },
  {
    label: 'Data Processing',
    code: `# Process input data
data = market_data.get('close', [])
if data:
    average = sum(data) / len(data)
    result = {'average': average}
else:
    result = {'error': 'No data available'}`,
  },
  {
    label: 'Conditional Logic',
    code: `# Conditional decision making
signal = analysis_result.get('signal', 'HOLD')
confidence = analysis_result.get('confidence', 0)

if signal == 'BUY' and confidence > 0.7:
    action = 'EXECUTE_BUY'
elif signal == 'SELL' and confidence > 0.7:
    action = 'EXECUTE_SELL'
else:
    action = 'WAIT'

result = {
    'action': action,
    'original_signal': signal,
    'confidence': confidence
}`,
  },
  {
    label: 'DataFrame Processing',
    code: `# Process with pandas (if available)
import pandas as pd

df = pd.DataFrame(market_data)
df['sma_20'] = df['close'].rolling(20).mean()
df['signal'] = df['close'] > df['sma_20']

result = {
    'latest_signal': df['signal'].iloc[-1],
    'sma_value': df['sma_20'].iloc[-1],
    'data': df.tail(5).to_dict('records')
}`,
  },
  {
    label: 'Error Handling',
    code: `# Safe processing with error handling
try:
    value = market_data['close'][-1]
    processed = value * 1.05
    result = {'processed_value': processed, 'status': 'success'}
except (KeyError, IndexError) as e:
    result = {'error': str(e), 'status': 'failed'}
except Exception as e:
    result = {'error': f'Unexpected error: {e}', 'status': 'failed'}`,
  },
];

// Available built-in modules and functions
const AVAILABLE_MODULES = [
  { name: 'json', description: 'JSON encoding/decoding' },
  { name: 'math', description: 'Mathematical functions' },
  { name: 'datetime', description: 'Date and time handling' },
  { name: 'Decimal', description: 'Precise decimal arithmetic' },
  { name: 'statistics', description: 'Statistical functions' },
  { name: 'collections', description: 'Container datatypes' },
  { name: 're', description: 'Regular expressions' },
  { name: 'numpy/np', description: 'Numerical computing (if installed)' },
  { name: 'pandas/pd', description: 'Data analysis (if installed)' },
];

export const PythonCodeEditor: React.FC<PythonCodeEditorProps> = ({
  value,
  onChange,
  disabled = false,
  availableInputs = [],
  minHeight = 400,
  maxHeight = 800,
  placeholder,
  onTestRun,
  testRunLoading = false,
}) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const [showHelp, setShowHelp] = React.useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Handle editor mount
  const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Configure Python-specific autocomplete with input variables
    monaco.languages.registerCompletionItemProvider('python', {
      provideCompletionItems: (model: any, position: any) => {
        const suggestions: any[] = [];

        // Add input variables as suggestions
        availableInputs.forEach((input) => {
          suggestions.push({
            label: input.name,
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: input.name,
            detail: `Input variable${input.type ? ` (${input.type})` : ''}`,
            documentation: input.description || `Injected input: ${input.name}`,
          });
        });

        // Add special variables
        suggestions.push({
          label: 'inputs',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: 'inputs',
          detail: 'Dict containing all inputs',
          documentation: 'Dictionary containing all input variables. Use inputs.keys() to see available inputs.',
        });

        suggestions.push({
          label: 'context',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: 'context',
          detail: 'Execution context',
          documentation: 'Contains: expert_name, expert_id, user_id, backtesting_mode',
        });

        suggestions.push({
          label: 'result',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: 'result = {\n    \n}',
          detail: 'Output variable (required)',
          documentation: 'Set this variable with your output dictionary. This is required!',
        });

        return { suggestions };
      },
    });

    // Add keyboard shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      // Trigger save (handled by parent)
    });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
      // Format code
      editor.getAction('editor.action.formatDocument')?.run();
    });
  }, [availableInputs]);

  // Copy code to clipboard
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    toast.success('Code copied to clipboard');
  }, [value]);

  // Insert snippet at cursor
  const insertSnippet = useCallback((code: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const selection = editor.getSelection();
      const id = { major: 1, minor: 1 };
      const op = {
        identifier: id,
        range: selection,
        text: code,
        forceMoveMarkers: true,
      };
      editor.executeEdits('snippet', [op]);
      editor.focus();
    } else {
      // Fallback: append to end
      onChange(value + '\n\n' + code);
    }
  }, [value, onChange]);

  // Default placeholder code
  const defaultPlaceholder = `# CODE Expert - Python Execution
# 
# Available variables:
${availableInputs.map(i => `#   - ${i.name}${i.type ? ` (${i.type})` : ''}`).join('\n') || '#   (define inputs in the sidebar)'}
#   - inputs: dict of all inputs
#   - context: execution context
#
# Pre-imported: json, math, datetime, Decimal
# Optional: numpy (np), pandas (pd)
#
# IMPORTANT: Set 'result' variable with your output dict

# Example:
# processed = market_data.get('close', 0) * 1.1
# result = {'processed_value': processed}

result = {
    'status': 'success',
    'message': 'Hello from CODE expert!'
}`;

  return (
    <Box>
      {/* Toolbar */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 1, 
          mb: 0, 
          borderBottomLeftRadius: 0, 
          borderBottomRightRadius: 0,
          borderBottom: 'none',
          bgcolor: isDark ? 'grey.900' : 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Python Code
          </Typography>
          <Chip 
            label="Python 3" 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Snippet buttons */}
          <Tooltip title="Insert code snippet">
            <Box>
              {CODE_SNIPPETS.slice(0, 3).map((snippet) => (
                <Chip
                  key={snippet.label}
                  label={snippet.label}
                  size="small"
                  onClick={() => insertSnippet(snippet.code)}
                  disabled={disabled}
                  sx={{ 
                    mr: 0.5, 
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    height: 24,
                    '&:hover': { bgcolor: 'primary.light', color: 'white' }
                  }}
                />
              ))}
            </Box>
          </Tooltip>

          <Tooltip title="Copy code">
            <IconButton size="small" onClick={handleCopy} disabled={!value}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Toggle help">
            <IconButton size="small" onClick={() => setShowHelp(!showHelp)}>
              {showHelp ? <ExpandLessIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {onTestRun && (
            <Tooltip title="Test run code">
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<PlayArrowIcon />}
                onClick={onTestRun}
                disabled={disabled || testRunLoading || !value}
                sx={{ ml: 1 }}
              >
                {testRunLoading ? 'Running...' : 'Test'}
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {/* Help Panel */}
      <Collapse in={showHelp}>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            borderRadius: 0,
            borderTop: 'none',
            borderBottom: 'none',
            bgcolor: 'info.50',
          }}
        >
          <Stack spacing={2}>
            {/* Available Inputs */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Your Input Variables
              </Typography>
              {availableInputs.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {availableInputs.map((input) => (
                    <Chip
                      key={input.name}
                      label={input.name}
                      size="small"
                      color="success"
                      onClick={() => insertSnippet(input.name)}
                      sx={{ cursor: 'pointer', mb: 0.5 }}
                    />
                  ))}
                  <Chip label="inputs" size="small" color="default" onClick={() => insertSnippet('inputs')} sx={{ cursor: 'pointer', mb: 0.5 }} />
                  <Chip label="context" size="small" color="default" onClick={() => insertSnippet('context')} sx={{ cursor: 'pointer', mb: 0.5 }} />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No inputs defined yet. Add inputs in the sidebar to use them in your code.
                </Typography>
              )}
            </Box>

            {/* Available Modules */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Available Modules
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {AVAILABLE_MODULES.map((mod) => (
                  <Tooltip key={mod.name} title={mod.description}>
                    <Chip
                      label={mod.name}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* More Snippets */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                More Code Snippets
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {CODE_SNIPPETS.map((snippet) => (
                  <Chip
                    key={snippet.label}
                    label={snippet.label}
                    size="small"
                    color="secondary"
                    onClick={() => insertSnippet(snippet.code)}
                    disabled={disabled}
                    sx={{ cursor: 'pointer', mb: 0.5 }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Collapse>

      {/* Monaco Editor */}
      <Box 
        sx={{ 
          border: 1, 
          borderColor: 'divider',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 1,
          borderBottomRightRadius: 1,
          overflow: 'hidden',
          minHeight: minHeight,
          maxHeight: maxHeight,
          resize: 'vertical',
          '& .monaco-editor': {
            paddingTop: '8px',
          }
        }}
      >
        <Editor
          height={`${minHeight}px`}
          defaultLanguage="python"
          value={value || ''}
          onChange={(val) => onChange(val || '')}
          theme={isDark ? 'vs-dark' : 'vs-light'}
          options={{
            minimap: { enabled: value.length > 500 },
            lineNumbers: 'on',
            wordWrap: 'on',
            scrollBeyondLastLine: false,
            fontSize: 14,
            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
            fontLigatures: true,
            padding: { top: 10, bottom: 10 },
            automaticLayout: true,
            readOnly: disabled,
            tabSize: 4,
            insertSpaces: true,
            folding: true,
            foldingHighlight: true,
            showFoldingControls: 'always',
            bracketPairColorization: { enabled: true },
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            autoIndent: 'full',
            formatOnPaste: true,
            formatOnType: true,
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            snippetSuggestions: 'top',
            renderWhitespace: 'selection',
            guides: {
              indentation: true,
              bracketPairs: true,
            },
          }}
          onMount={handleEditorDidMount}
        />
      </Box>

      {/* Footer with keyboard shortcuts */}
      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {value ? `${value.split('\n').length} lines` : 'Empty'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ctrl+Space: Autocomplete • Ctrl+Shift+F: Format • Tab: Indent
        </Typography>
      </Box>
    </Box>
  );
};

export default PythonCodeEditor;
